import { format2TimeStamp } from '@/utils/date';
import { MemberType, OccType } from '@/config/constant';
import { parseYuan2Fen } from '@/utils/number';
import baseService from '@/api/checkInBase';
import {
  createDefaultRentTime,
  findItemByKey,
  flatPriceList,
  getRentTimeInfo,
  createGuestItem,
} from '@/redux/actions/reservation/helper';
import { MainState, ReducerState } from '@/redux/reducers/reservation/checkIn';
import { createTargetGetter, TargetType } from '@/components/Reservation/PopupTarget';
import { CheckInGuestInfo } from '@/redux/reducers/reservation/base.d';
import { parseAreaMobileStr } from '@/components/BaseMobileInput/helper';

// 默认数据
export const getDefaultState: () => MainState = () => {
  const { dayTypeTimeInfo, hourTypeTimeInfo } = createDefaultRentTime();
  return {
    commonInfo: {
      memberLevel: undefined,
      occType: OccType.DayUse,
    },
    allGuestList: [createGuestItem({ memberType: MemberType.HouseOwner })], // 默认有一个房主
    dayTypeTimeInfo,
    hourTypeTimeInfo,
    channelInfo: {
      otaPaymentAmount: '',
      invoiceAmount: '',
    },
    remarkInfo: {
      remark: '',
      checkInRemark: '',
    },
    defaultSelectRoom: [],
  };
};

// 追加入住 原单数据
export const getStateFromMain: (orderId: string) => Promise<MainState> = async (orderId) => {
  const res = await baseService.getOrderDetail(orderId);
  const orderInfo = res.data;
  const guestList: CheckInGuestInfo[] = orderInfo.subOrderOccRespList || [];
  const masterInfo =
    findItemByKey(guestList, 'memberType', MemberType.HouseOwner) || <CheckInGuestInfo>{}; // 房主信息
  const defaultState = getDefaultState();
  return {
    ...defaultState,
    commonInfo: {
      ...defaultState.commonInfo,
      appendOrderId: orderId,
      memberLevel: masterInfo.memberLevel,
    },
    // 房主信息 取外层和master 做兼容
    allGuestList: [
      createGuestItem({
        memberType: MemberType.HouseOwner,
        name: orderInfo.name || masterInfo.name,
        certType: masterInfo.certType,
        certNo: masterInfo.certNo,
        mobile: orderInfo.mobile || masterInfo.mobile,
        // memberFlag: orderInfo.memberFlag || false,
        memberLevel: orderInfo.memberLevel,
        balance: masterInfo.balance,
        wyuId: orderInfo.wyuId || masterInfo.wyuId,
        remark: orderInfo.remark || masterInfo.remark,
        email: masterInfo.email,
      }), // 房主
      createGuestItem({
        memberType: MemberType.CheckIn,
      }),
    ],
  };
};

export const formatToSubmitData: (data: ReducerState) => { [key: string]: unknown } = (data) => {
  const {
    commonInfo,
    allGuestList,
    channelInfo,
    dayTypeTimeInfo,
    hourTypeTimeInfo,
    rentRoomInfos,
    channelList,
    strategyList,
    roomPriceList,
    remarkInfo,
  } = data;
  const rentTimeInfo = getRentTimeInfo({ dayTypeTimeInfo, hourTypeTimeInfo }, commonInfo.occType);
  const { channelId, strategyId, channelOrderNo, otaPaymentAmount, invoiceAmount } = channelInfo;
  const sourceChannelName = findItemByKey(channelList, 'id', channelId)?.name;
  const priceStrategyName = findItemByKey(strategyList, 'id', strategyId)?.showName;
  const { cachePriceMap } = flatPriceList(roomPriceList);
  // 宾客入住房间信息
  const guestRoomIdMap: {
    [key: string]: { roomId: number; roomNo: string; roomTypeId: number };
  } = {};
  const resvNo = commonInfo.appendOrderId;
  const subOrderCreateVOS = (rentRoomInfos || []).map((room) => {
    const {
      roomId,
      roomNo,
      roomTypeId,
      roomTypeName,
      roomPriceList: itemPriceList,
      guestList,
      deposit,
    } = room;
    const strategyPriceMap = cachePriceMap[roomTypeId] || {};

    (guestList || []).forEach((guestId) => {
      // 宾客所在房间信息
      guestRoomIdMap[guestId] = { roomId, roomNo, roomTypeId };
    });

    return {
      roomId,
      roomNo,
      roomTypeId,
      roomTypeName,
      sourceChannelId: channelId,
      sourceChannelName,
      deposit: parseYuan2Fen(deposit),
      subOrderRoomPriceCreateVOS: itemPriceList.map((dayInfo) => {
        const {
          rentDate,
          inputPrice,
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          couponSubType,
        } = dayInfo;
        return {
          rentDate: format2TimeStamp(rentDate),
          price: parseYuan2Fen(inputPrice),
          priceStrategyId: strategyId,
          strategyPrice: parseYuan2Fen(strategyPriceMap[rentDate]),
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          couponSubType,
        };
      }),
    };
  });
  const subOrderOccCreateVOS = [];
  // 是否只有一件房间 只有一间房时 默认所有宾客都住这一间
  const isOnlyOneRoom = subOrderCreateVOS.length === 1;
  (allGuestList || []).forEach((item) => {
    const { name, indexId, memberType } = item;
    if ((memberType === MemberType.HouseOwner && resvNo) || !name) return; // 追加入住 不提交房主 信息不全 不提交
    const { roomId, roomNo } = isOnlyOneRoom ? subOrderCreateVOS[0] : guestRoomIdMap[indexId] || {};
    const { mobileStr } = parseAreaMobileStr(item.mobile);
    subOrderOccCreateVOS.push({
      birthday: item.birthday,
      certNo: item.certNo,
      certType: item.certType,
      certificateEndTime: item.certificateEndTime,
      certificateLocation: item.certificateLocation,
      certificateStartTime: item.certificateStartTime,
      issuingAuthority: item.issuingAuthority,
      idCardReadStatus: item.source === 'READ' ? 1 : 2,
      // memberFlag: item.memberFlag,
      memberLevel: item.memberLevel,
      mobile: mobileStr ? item.mobile : null,
      email: item.email,
      nation: item.nation,
      remark: item.remark,
      sex: item.sex,
      wyuId: item.wyuId,
      memberType,
      name,
      roomId,
      roomNo,
    });
  });
  return {
    resvNo,
    subOrderOccCreateVOS,
    occType: rentTimeInfo.occType,
    occDuration: rentTimeInfo.rentTime,
    expectedDepartureTime: format2TimeStamp(rentTimeInfo.expectedDepartureTime),
    sourceChannelId: channelId,
    sourceChannelName,
    priceStrategyId: strategyId,
    priceStrategyName,
    channelOrderNo,
    otaPaymentAmount: parseYuan2Fen(otaPaymentAmount),
    invoiceAmount: parseYuan2Fen(invoiceAmount),
    subOrderCreateVOS,
    remark: remarkInfo.remark,
    checkinRemark: remarkInfo.checkInRemark,
  };
};

// 定位节点
export const getPopupContainer = createTargetGetter(TargetType.CheckIn);
